import { useTranslation } from "react-i18next";
import { Linkedin, Github } from "lucide-react";
import { useLanguageNavigation } from "@/components/LanguageRouter";
import logo from "@/assets/logo.png";

const social = [
  { name: "LinkedIn", href: "https://www.linkedin.com/company/shelpuk-ai-technology-consulting", icon: Linkedin },
  { name: "GitHub", href: "https://github.com/Shelpuk-AI-Technology-Consulting", icon: Github },
];

export function Footer() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();

  const services = [
    { nameKey: "services.aiDiscovery.title", href: "/services/consulting" },
    { nameKey: "services.fixedPrice.title", href: "/services/fixed-price" },
    { nameKey: "services.enablement.title", href: "/services/enablement" },
    { nameKey: "services.teams.title", href: "/services/teams" },
  ];

  const industries = [
    { nameKey: "industries.healthcare.title", href: "/industries/pharma" },
    { nameKey: "industries.finance.title", href: "/industries/finance" },
    { nameKey: "industries.enterprise.title", href: "/industries/enterprise" },
    { nameKey: "industries.defence.title", href: "/industries/defence" },
  ];

  const openSource = [
    { nameKey: "footer.openSource.kindly", href: "/open-source/kindly-web-search" },
    { nameKey: "footer.openSource.lad", href: "/open-source/lad-code-review" },
    { nameKey: "footer.openSource.tdd", href: "/open-source/tdd" },
  ];

  const resources = [
    { nameKey: "nav.caseStudies", href: "/case-studies" },
    { nameKey: "nav.blog", href: "/blog" },
    { nameKey: "nav.insights", href: "/insights" },
    { nameKey: "nav.euPrivacy", href: "/sovereign-ai" },
  ];

  const legal = [
    { nameKey: "footer.legal.terms", href: "/terms" },
    { nameKey: "footer.legal.privacy", href: "/privacy" },
  ];

  const renderLinkList = (items: { nameKey: string; href: string }[]) => (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item.nameKey}>
          <a
            href={getLocalizedPath(item.href)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t(item.nameKey)}
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <footer className="border-t border-border/50" role="contentinfo">
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-6">
          {/* Company Info — spans full width on mobile */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-1 -mt-[23px]">
            <a href={getLocalizedPath("/")} className="inline-flex items-center gap-2" aria-label="Shelpuk & Company Home">
              <img
                src={logo}
                alt="Shelpuk & Company"
                width={400}
                height={74}
                decoding="async"
                className="h-10 w-auto object-contain"
              />
            </a>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 flex gap-3">
              {social.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label={`${item.name}`}
                >
                  <item.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <nav aria-label={t("footer.sections.services")}>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t("footer.sections.services")}</h3>
            {renderLinkList(services)}
          </nav>

          {/* Industries */}
          <nav aria-label={t("footer.sections.industries")}>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t("footer.sections.industries")}</h3>
            {renderLinkList(industries)}
          </nav>

          {/* Open Source */}
          <nav aria-label={t("footer.sections.openSource")}>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t("footer.sections.openSource")}</h3>
            {renderLinkList(openSource)}
          </nav>

          {/* Resources */}
          <nav aria-label={t("footer.sections.resources")}>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t("footer.sections.resources")}</h3>
            {renderLinkList(resources)}
          </nav>

          {/* Legal */}
          <nav aria-label={t("footer.sections.legal")}>
            <h3 className="text-sm font-semibold text-foreground mb-4">{t("footer.sections.legal")}</h3>
            {renderLinkList(legal)}
          </nav>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("footer.compliance")}
          </p>
        </div>
      </div>
    </footer>
  );
}
